import { createGlobalState, useLocalStorage } from '@vueuse/core';
import { computed, ref } from 'vue';

interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const MAX_ATTEMPTS = 3; // if the user dismisses the prompt 3 times, we will not show it again for 14 days
const COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function isStandaloneDisplay(): boolean {
    return (
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true
    );
}

function isMobileDevice(): boolean {
    const ua = window.navigator.userAgent;
    const isMobileUa = /android|iphone|ipad|ipod/i.test(ua);
    // iPadOS 13+ reports "Macintosh" in the UA, and desktop touch monitors exist too,
    // so require both a coarse pointer and no fine pointer (mouse) to catch those cases
    // without flagging a touchscreen laptop as mobile.
    const isTouchOnly = window.matchMedia('(pointer: coarse)').matches && !window.matchMedia('(pointer: fine)').matches;
    return isMobileUa || isTouchOnly;
}

function isIosSafariBrowser(): boolean {
    const ua = window.navigator.userAgent;
    // iPadOS 13+ Safari reports itself as "Macintosh" in the UA string, so a
    // touch-capable "Mac" is the only way to distinguish an iPad from a real desktop.
    const isSpoofedIpad = /macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
    const isIos = (/iphone|ipad|ipod/i.test(ua) || isSpoofedIpad) && !('MSStream' in window);
    const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
    return isIos && isSafari;
}

export const usePwaInstall = createGlobalState(() => {
    const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null);
    const isInstalled = useLocalStorage('pwaInstall_installed', false);
    const lastPromptedAt = useLocalStorage<number | null>('pwaInstall_lastPromptedAt', null);
    const attemptCount = useLocalStorage('pwaInstall_attemptCount', 0);

    const isIosSafari = isIosSafariBrowser();
    const isStandalone = isStandaloneDisplay();
    const isMobile = isMobileDevice();

    // Suppress the browser's own mini-infobar so we control when the install UI appears.
    window.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault();
        deferredPrompt.value = event as BeforeInstallPromptEvent;
    });

    window.addEventListener('appinstalled', () => {
        isInstalled.value = true;
        deferredPrompt.value = null;
    });

    const isWithinAttemptBudget = computed(() => {
        if (attemptCount.value >= MAX_ATTEMPTS) {
            return false;
        }
        if (lastPromptedAt.value === null) {
            return true;
        }
        return Date.now() - lastPromptedAt.value >= COOLDOWN_MS;
    });

    const canShowInstallBanner = computed(() => {
        if (!isMobile || isStandalone || isInstalled.value) {
            return false;
        }
        if (!isWithinAttemptBudget.value) {
            return false;
        }
        return deferredPrompt.value !== null || isIosSafari;
    });

    // Shared bookkeeping for every way the user can pass on the prompt: declining the
    // native dialog or dismissing the iOS instructions banner.
    function recordAttempt() {
        attemptCount.value += 1;
        lastPromptedAt.value = Date.now();
    }

    async function promptInstall() {
        if (!deferredPrompt.value) {
            return;
        }
        const promptEvent = deferredPrompt.value;
        recordAttempt();
        await promptEvent.prompt();
        const { outcome } = await promptEvent.userChoice;
        if (outcome === 'accepted') {
            isInstalled.value = true;
        }
        deferredPrompt.value = null;
    }

    function dismissBanner() {
        recordAttempt();
    }

    return {
        canShowInstallBanner,
        supportsNativePrompt: computed(() => deferredPrompt.value !== null),
        isIosSafari,
        promptInstall,
        dismissBanner,
    };
});
